import React, { useEffect } from "react";

import ErrorBoundary from "components/ErrorBoundary";
import { LoaderSmall } from "components/Loader";
import { AddCardModal } from "components/CardManagement";
import EmailRequiredModal from "components/GlobalModals/EmailRequiredModal";
import InsufficientPointsModal from "components/GlobalModals/InsufficientPointsModal";
import { useScreenTracking } from "hooks/useScreenTracking";
import {
  trackResumoCompraEvent,
  trackViewCartMarketplaceItems,
  trackAddShippingInfoMarketplaceItems,
} from "utils/gtm";

import { useMarketplaceCheckout } from "./hooks/useMarketplaceCheckout";
import CheckoutHeader from "./_components/CheckoutHeader";
import FinancialSummary from "./_components/FinancialSummary";
import CouponField from "./_components/CouponField";
import PointsSlider from "./_components/PointsSlider";
import PixSection from "./_components/PixSection";
import CardSection from "./_components/CardSection";
import FinalizeButton from "./_components/FinalizeButton";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Page, SectionLabel, CheckoutLayout, FixedFooter, InsufficientPointsWarning } from "./styles";

export default function MarketplaceCheckout() {
  const checkout = useMarketplaceCheckout();
  const isDesktop = useMediaQuery("(min-width:1024px)");
  const { loading, dataCheckout } = checkout;

  useScreenTracking();

  useEffect(() => {
    if (loading) return;
    const items = dataCheckout?.cart?.items || [];
    trackViewCartMarketplaceItems(items);
    trackAddShippingInfoMarketplaceItems(
      items,
      dataCheckout?.cart?.shipping_by_brand,
    );
  }, [loading, dataCheckout]);

  if (loading) return <LoaderSmall />;

  const summary = dataCheckout?.summary || {};
  const { financialSummary } = checkout;
  const couponInfo = summary.coupon || null;
  const couponForSummary =
    couponInfo && financialSummary.discountMoney > 0
      ? {
          discountMoney: financialSummary.discountMoney,
          discountPoints: financialSummary.discountPoints,
          code: couponInfo.code || couponInfo.name || "",
        }
      : null;

  const summaryBlock = (
    <FinancialSummary
      displayMode={checkout.displayMode}
      subtotalMoney={financialSummary.subtotalMoney}
      subtotalPoints={financialSummary.subtotalPoints}
      shippingMoney={financialSummary.shippingMoney}
      shippingPoints={financialSummary.shippingPoints}
      coupon={couponForSummary}
      totalMoney={financialSummary.totalMoney}
      totalPoints={financialSummary.totalPoints}
    />
  );

  const finalizeBlock = (
    <FinalizeButton
      disabled={!checkout.canFinalize}
      loading={checkout.loadingCheckout}
      onClick={checkout.finalize}
    />
  );

  const paymentBlocks = (
    <>
      {checkout.couponsEnabled && (
        <>
          <CouponField
            value={checkout.coupon}
            setValue={checkout.setCoupon}
            applied={Boolean(checkout.couponInfo)}
            info={checkout.couponInfo}
            error={checkout.couponError}
            loading={checkout.loadingCoupon}
            onApply={checkout.applyCoupon}
            onRemove={checkout.removeCoupon}
            discountAmount={financialSummary.discountMoney}
          />
        </>
      )}

      {checkout.hasInsufficientPoints && (
        <InsufficientPointsWarning role="alert">
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              d="M10 1.667a8.333 8.333 0 1 0 0 16.666 8.333 8.333 0 0 0 0-16.666Zm0 12.5a.833.833 0 1 1 0-1.667.833.833 0 0 1 0 1.667Zm.833-3.334H9.167V5.833h1.666v5Z"
              fill="currentColor"
            />
          </svg>
          <span>
            Você precisa possuir a quantidade mínima de{" "}
            <strong>
              {Math.round(checkout.minPoints).toLocaleString("pt-BR")} pontos
            </strong>{" "}
            para concluir esta compra. Seu saldo atual é de{" "}
            <strong>
              {Math.round(checkout.userBalance).toLocaleString("pt-BR")} pontos
            </strong>.
          </span>
        </InsufficientPointsWarning>
      )}

      <SectionLabel>Escolha a forma de pagamento</SectionLabel>
      {checkout.showPointsSlider && (
        <PointsSlider
          pontos={checkout.sliderPontos}
          reais={checkout.sliderReais}
          min={checkout.minPontosSlider}
          max={checkout.pontosDisponiveis}
          onChange={checkout.handlePontosPreview}
          onTrackChange={(p, r) => {
            checkout.handlePontosCommit(p);
            trackResumoCompraEvent({
              title: "ajustar-pontos",
              pontos: p,
              reais: r,
            });
          }}
        />
      )}
      {checkout.paymentFlags?.pix !== false && (
        <PixSection
          selected={checkout.paymentMethod === "pix"}
          onSelect={(v) => {
            checkout.setPaymentMethod(v ? "pix" : null);
            if (v) checkout.setSelectedCardId(null);
          }}
          disabled={checkout.reaisRestante === 0}
        />
      )}
      {checkout.paymentFlags?.card !== false && (
        <CardSection
          cards={checkout.cards}
          selectedCardId={checkout.selectedCardId}
          onSelectCard={(id) => {
            checkout.setSelectedCardId(id);
            checkout.setPaymentMethod("card");
          }}
          onRemoveCard={checkout.cardMgmt.onClickDelCard}
          onAddCard={checkout.cardMgmt.openAddCardModal}
          installments={checkout.installments}
          installmentsSelected={checkout.installmentsSelected}
          onPickInstallment={checkout.setInstallmentsSelected}
          disabled={checkout.reaisRestante === 0}
        />
      )}
      <AddCardModal cardMgmt={checkout.cardMgmt} cards={checkout.cards} />
    </>
  );

  return (
    <ErrorBoundary>
      <div className="wl-page">
        <Page>
          <CheckoutHeader />

          {isDesktop ? (
            <CheckoutLayout>
              <div className="co-methods">{paymentBlocks}</div>
              <div className="co-summary">
                {summaryBlock}
                {finalizeBlock}
              </div>
            </CheckoutLayout>
          ) : (

            <>
              {paymentBlocks}
            </>
          )}
        </Page>
        {!isDesktop && (
          <FixedFooter>
            <FinancialSummary
              displayMode={checkout.displayMode}
              subtotalMoney={financialSummary.subtotalMoney}
              subtotalPoints={financialSummary.subtotalPoints}
              shippingMoney={financialSummary.shippingMoney}
              shippingPoints={financialSummary.shippingPoints}
              coupon={couponForSummary}
              totalMoney={financialSummary.totalMoney}
              totalPoints={financialSummary.totalPoints}
            >
              <FinalizeButton
                disabled={!checkout.canFinalize}
                loading={checkout.loadingCheckout}
                onClick={checkout.finalize}
                inline
              />
            </FinancialSummary>
          </FixedFooter>
        )}
        <EmailRequiredModal
          show={checkout.showEmailRequired}
          onClose={() => checkout.setShowEmailRequired(false)}
          onRegisterEmail={() => checkout.setShowEmailRequired(false)}
          onContinue={() => checkout.setShowEmailRequired(false)}
        />
        <InsufficientPointsModal
          show={checkout.showInsufficientPoints}
          onClose={() => checkout.setShowInsufficientPoints(false)}
          minPoints={Number(checkout.minPoints || checkout.pontosValue || 0)}
          userBalance={checkout.userBalance}
        />
      </div>
    </ErrorBoundary>
  );
}
